/**
 * iter189 - "Open my IEPs" nav pill.
 *
 * Persistent Navbar item visible whenever the logged-in educator has >=1
 * functional IEP saved. Hits `/api/functional-iep/mine` on mount; hidden
 * silently when the count is 0 so it never adds clutter for new users.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ChevronDown } from "lucide-react";
import api from "@/lib/api";

export default function MyIepsNavItem() {
  const [ieps, setIeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/functional-iep/mine");
        if (alive) setIeps(r.data?.ieps || []);
      } catch (e) { /* 401 for unauth or 5xx just hide */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!loaded || ieps.length === 0) return null;

  return (
    <div className="relative" ref={ref} data-testid="nav-my-ieps">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[#2D312E] hover:bg-[#F4F1EA] text-[15px] font-medium"
        data-testid="nav-my-ieps-toggle"
        aria-expanded={open}
      >
        <ClipboardList className="h-4 w-4" /> My IEPs
        <span className="text-xs bg-[#4F7660] text-white rounded-full px-1.5 py-0.5 leading-none">{ieps.length}</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-[280px] rounded-2xl border border-[#2D312E]/10 bg-white shadow-lg p-2 z-30"
          data-testid="nav-my-ieps-menu"
        >
          {ieps.map((it) => (
            <Link
              key={it.id}
              to={`/educator/functional-iep/${it.id}`}
              onClick={() => setOpen(false)}
              data-testid={`nav-my-iep-item-${it.id}`}
              className="block px-3 py-2 rounded-lg hover:bg-[#F4F1EA]"
            >
              <div className="font-semibold text-sm text-[#1F2320]">{it.student_name}</div>
              <div className="text-xs text-[#6B7276]">
                {it.total_goals} goals · updated {it.updated_at ? new Date(it.updated_at).toLocaleDateString() : "recently"}
              </div>
            </Link>
          ))}
          {/* iter210 - weekly-log entry point lives at the bottom of the
              same dropdown so an educator with active IEPs always has a
              one-tap route into the sticky weekly-log screen. */}
          <div className="border-t border-[#2D312E]/10 mt-2 pt-2">
            <Link
              to="/educator/weekly-log"
              onClick={() => setOpen(false)}
              data-testid="nav-weekly-log-item"
              className="block px-3 py-2 rounded-lg hover:bg-[#F4F1EA]"
            >
              <div className="font-semibold text-sm text-[#2C5847]">Log this week's progress →</div>
              <div className="text-xs text-[#6B7276]">One row per goal, batch save.</div>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
